import React, { useRef, useState } from 'react';
import { Button, Modal, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { markingApi } from '../../api/marking';
import DataMatrixImage from './DataMatrixImage';

interface Props {
  markingCode: string;
  statusHint?: string;
  statusError?: boolean;
  inBuffer?: boolean;
  // Марка без криптохвоста (rev. 2026-09-17 №7): красный текст — приоритет над
  // оранжевым «в буфере»; сохранение заказа с ней отсечёт backend (HTTP 400)
  hasFullCode?: boolean;
  onRemove?: () => void;
}

const LONG_PRESS_MS = 800;

const MarkCodeLink: React.FC<Props> = ({ markingCode, statusHint, statusError, inBuffer, hasFullCode, onRemove }) => {
  const [open, setOpen] = useState(false);
  const [rawCode, setRawCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = async () => {
    setOpen(true);
    setRawCode(null);
    setLoading(true);
    try {
      const { data } = await markingApi.getCodeByMarkingCode(markingCode);
      setRawCode(data.raw_code);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить полный код марки');
    } finally {
      setLoading(false);
    }
  };

  const startEmergencyPress = () => {
    if (!onRemove) return;
    pressTimer.current = setTimeout(() => {
      pressTimer.current = null;
      Modal.confirm({
        title: 'Аварийное удаление марки',
        content:
          'Вы пытаетесь удалить марку без сканированной марки в буфере сканирования — ' +
          'это экстренная функция, нажмите «Удалить», чтобы подтвердить.',
        okText: 'Удалить',
        okButtonProps: { danger: true },
        cancelText: 'Отмена',
        centered: true,
        onOk: () => {
          setOpen(false);
          onRemove();
        },
      });
    }, LONG_PRESS_MS);
  };

  const cancelEmergencyPress = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
      message.info('Удерживайте кнопку для удаления');
    }
  };

  const incomplete = hasFullCode === false;
  // Rev. 2026-09-22 (OilChangePage №42 / ServiceOrderPage №15): марка «в порядке» —
  // зелёный (#52c41a), а не серый: цвет = индикатор «с маркой всё ок».
  const textColor = incomplete || statusError ? '#ff4d4f' : inBuffer ? '#fa8c16' : '#52c41a';
  const hint = incomplete
    ? 'Марка без криптохвоста — отсканируйте товар сканером, иначе заказ не сохранить'
    : statusHint;

  return (
    <>
      <style>{`@keyframes glow-pulse-orange { 0% { box-shadow: 0 0 0 0 rgba(250,173,20,0.7); } 70% { box-shadow: 0 0 0 10px rgba(250,173,20,0); } 100% { box-shadow: 0 0 0 0 rgba(250,173,20,0); } }`}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
        <div
          style={{
            fontSize: 12,
            color: textColor,
            wordBreak: 'break-all',
            cursor: 'pointer',
            textDecoration: 'underline',
            flex: 1,
            minWidth: 0,
            ...(inBuffer
              ? {
                  animation: 'glow-pulse-orange 1.5s ease-in-out infinite',
                  outline: '2px solid #faad14',
                  outlineOffset: '1px',
                  borderRadius: 4,
                }
              : {}),
          }}
          title={hint}
          onClick={handleClick}
        >
          Марка: {markingCode}
        </div>
        {onRemove && inBuffer && (
          <CloseOutlined
            style={{ color: '#ff4d4f', fontSize: 12, lineHeight: '18px', cursor: 'pointer' }}
            title="Удалить марку из заказа (марка в буфере сканирования)"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          />
        )}
      </div>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        centered
        width={360}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 16 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Загрузка полного кода...</div>
          ) : rawCode ? (
            <>
              <DataMatrixImage code={rawCode} scale={2} />
              <div style={{ marginTop: 12, wordBreak: 'break-all', fontSize: 12, userSelect: 'text' }}>
                {markingCode}
              </div>
            </>
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: '#ff4d4f' }}>
              Полный код марки не найден.
              <br />
              DataMatrix нельзя сгенерировать по обрезанному marking_code.
            </div>
          )}
          {onRemove && !inBuffer && (
            <Button
              danger
              size="small"
              style={{ marginTop: 16 }}
              onPointerDown={startEmergencyPress}
              onPointerUp={cancelEmergencyPress}
              onPointerLeave={cancelEmergencyPress}
            >
              Удалить (удерживать)
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
};

export default MarkCodeLink;
